import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogIn, LogOut, LayoutDashboard } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const links = [
  { id: "tentang", label: "Tentang" },
  { id: "program", label: "Program" },
  { id: "fasilitas", label: "Fasilitas" },
  { id: "pengajar", label: "Pengajar" },
  { id: "galeri", label: "Galeri" },
  { id: "artikel", label: "Artikel", to: "/artikel" },
  { id: "testimoni", label: "Testimoni" },
  { id: "pendaftaran", label: "Pendaftaran" },
];

// Robust section scroll that:
// 1. closes mobile menu first
// 2. waits for menu collapse animation so target's Y position is stable
// 3. uses Lenis (if available) otherwise falls back to native smooth scroll
function scrollToSection(id, options = {}) {
  const { delay = 0 } = options;
  const exec = () => {
    const el = document.getElementById(id);
    if (!el) return;
    const navOffset = 88; // sticky nav height
    const rect = el.getBoundingClientRect();
    const targetY = rect.top + window.scrollY - navOffset;
    const lenis = window.__lenis;
    if (lenis && typeof lenis.scrollTo === "function") {
      lenis.scrollTo(targetY, { duration: 1.1, immediate: false });
    } else {
      window.scrollTo({ top: targetY, behavior: "smooth" });
    }
  };
  if (delay > 0) setTimeout(exec, delay);
  else exec();
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  const isLogged = user && user.role;
  const dashPath = isLogged
    ? (user.role === "admin" ? "/admin" : user.role === "pembimbing" ? "/pembimbing" : "/santri")
    : "/login";

  const doLogout = () => {
    setOpen(false);
    logout();
    navigate("/", { replace: true });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const gotoSection = (id) => {
    if (onHome) {
      scrollToSection(id);
    } else {
      // navigate home with a hash so Landing can pick it up
      navigate(`/#${id}`);
    }
  };

  const handleNavDesktop = (id) => gotoSection(id);
  const handleNavMobile = (id) => {
    setOpen(false);
    if (onHome) {
      scrollToSection(id, { delay: 460 });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-parchment/85 backdrop-blur-md border-b border-gold/30" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="/"
          onClick={(e) => {
            if (onHome) {
              e.preventDefault();
              handleNavDesktop("hero");
            }
            // when not on home, let the browser navigate to /
          }}
          className="flex items-center gap-3 group"
          data-testid="nav-brand"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_generasi-flow/artifacts/fgw1jyfc_LOGO%20PONDOK%20GRQ%202022_alter.png"
            alt="Logo Pondok Tahfizh Generasi Rabbani Qurani"
            className="h-12 w-12 md:h-14 md:w-14 object-contain"
          />
          <div className="leading-tight hidden sm:block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-moss">Pondok Tahfizh</div>
            <div className="font-serif-display text-forest text-base md:text-lg">Generasi Rabbani Qurani</div>
          </div>
        </a>

        <ul className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <li key={l.id}>
              {l.to ? (
                <Link
                  to={l.to}
                  className="text-sm text-forest/80 hover:text-gold transition-colors duration-300 relative group"
                  data-testid={`nav-link-${l.id}`}
                >
                  {l.label}
                  <span className="absolute left-0 -bottom-1 w-0 group-hover:w-full h-px bg-gold transition-all duration-500" />
                </Link>
              ) : (
                <button
                  onClick={() => handleNavDesktop(l.id)}
                  className="text-sm text-forest/80 hover:text-gold transition-colors duration-300 relative group"
                  data-testid={`nav-link-${l.id}`}
                >
                  {l.label}
                  <span className="absolute left-0 -bottom-1 w-0 group-hover:w-full h-px bg-gold transition-all duration-500" />
                </button>
              )}
            </li>
          ))}
        </ul>

        <button
          onClick={() => gotoSection("pendaftaran")}
          className="hidden lg:inline-flex items-center px-5 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors duration-300"
          data-testid="nav-cta-daftar"
        >
          Daftar Santri
        </button>

        {isLogged ? (
          <div className="hidden lg:flex items-center gap-2">
            <Link
              to={dashPath}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/50 text-forest text-sm hover:bg-gold hover:text-forest transition-colors duration-300"
              data-testid="nav-cta-dashboard"
            >
              <LayoutDashboard size={14} /> Dashboard
            </Link>
            <button
              onClick={doLogout}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-forest text-sm hover:text-gold transition-colors duration-300"
              data-testid="nav-cta-logout"
              aria-label="Keluar"
            >
              <LogOut size={14} /> Keluar
            </button>
          </div>
        ) : (
          <Link
            to="/login"
            className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-forest/50 text-forest text-sm hover:bg-forest hover:text-parchment transition-colors duration-300"
            data-testid="nav-cta-login"
          >
            <LogIn size={14} /> Login
          </Link>
        )}

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-forest p-2"
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden overflow-hidden bg-parchment border-t border-gold/30"
            data-testid="nav-mobile-menu"
          >
            <ul className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <li key={l.id}>
                  {l.to ? (
                    <Link
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className="text-forest text-base w-full text-left py-1 block"
                      data-testid={`nav-mobile-link-${l.id}`}
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleNavMobile(l.id)}
                      className="text-forest text-base w-full text-left py-1"
                      data-testid={`nav-mobile-link-${l.id}`}
                    >
                      {l.label}
                    </button>
                  )}
                </li>
              ))}
              <li>
                <button
                  onClick={() => handleNavMobile("pendaftaran")}
                  className="mt-2 px-5 py-2.5 rounded-full bg-forest text-parchment text-sm w-fit"
                  data-testid="nav-mobile-cta-daftar"
                >
                  Daftar Santri
                </button>
              </li>
              {isLogged ? (
                <>
                  <li>
                    <Link
                      to={dashPath}
                      onClick={() => setOpen(false)}
                      className="inline-flex items-center gap-2 mt-2 px-5 py-2.5 rounded-full border border-gold/50 text-forest text-sm w-fit"
                      data-testid="nav-mobile-cta-dashboard"
                    >
                      <LayoutDashboard size={14} /> Dashboard
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={doLogout}
                      className="inline-flex items-center gap-2 mt-1 px-5 py-2.5 text-forest text-sm w-fit"
                      data-testid="nav-mobile-cta-logout"
                    >
                      <LogOut size={14} /> Keluar
                    </button>
                  </li>
                </>
              ) : (
                <li>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center gap-2 mt-2 px-5 py-2.5 rounded-full border border-forest/50 text-forest text-sm w-fit"
                    data-testid="nav-mobile-cta-login"
                  >
                    <LogIn size={14} /> Login
                  </Link>
                </li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
